import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  createInstance,
  connectInstance,
  checkInstanceStatus,
  InstanceStatus,
  getUniqueInstanceName
} from '../../../../services/whatsappService';
import { saveCredential } from '../../../../services/credentialsService';
import toast from 'react-hot-toast';


interface WhatsappConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnect: (instanceName: string, instanceToken: string) => void;
  qrCodeUrl?: string;
}

const POLLING_INTERVAL_STATUS = 5000; // 5 segundos - poll de status
const QR_TIMEOUT_SECONDS = 120; // 2 minutos - timeout do QR code

const WhatsappConnectModal: React.FC<WhatsappConnectModalProps> = ({
  isOpen,
  onClose,
  onConnect,
}) => {
  const { t } = useTranslation();
  const [connectMode, setConnectMode] = useState<'QR_CODE' | 'MANUAL_TOKEN'>('QR_CODE');
  const [baseInstanceName, setBaseInstanceName] = useState('');
  const [uniqueInstanceName, setUniqueInstanceName] = useState('');
  const [instanceToken, setInstanceToken] = useState(''); // Token UAZAPI da instância
  const [step, setStep] = useState<'INPUT' | 'QR_CODE'>('INPUT');
  const [qrCodeUrl, setQrCodeUrl] = useState<string | undefined>(undefined);
  const [pairCode, setPairCode] = useState<string | undefined>(undefined);
  const [isGenerating, setIsGenerating] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<InstanceStatus['status']>('DISCONNECTED');
  const [qrTimer, setQrTimer] = useState(QR_TIMEOUT_SECONDS);

  // Estados para conexão manual via Token
  const [manualToken, setManualToken] = useState('');
  const [manualInstanceName, setManualInstanceName] = useState('');
  const [isValidatingToken, setIsValidatingToken] = useState(false);
  const [tokenVerificationResult, setTokenVerificationResult] = useState<{
    status: InstanceStatus['status'];
    message: string;
    instanceName?: string;
  } | null>(null);

  // Refs para limpar os intervalos
  const statusPollingRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);

  // Limpa todos os intervalos
  const clearPolling = useCallback(() => {
    if (statusPollingRef.current) {
      clearInterval(statusPollingRef.current);
      statusPollingRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Inicia o contador regressivo do QR (2 minutos)
  const startQrTimer = useCallback(() => {
    setQrTimer(QR_TIMEOUT_SECONDS);
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = window.setInterval(() => {
      setQrTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          timerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Salva credenciais e notifica o componente pai
  const handleSuccessfulConnection = useCallback(async (token: string, name: string, uniqueName: string) => {
    await saveCredential('whatsapp', {
      api_key: token,
      is_active: true,
      instance_details: { name, uniqueName, status: 'connected' }
    });
    onConnect(name, token);
    toast.success(t('Instância WhatsApp conectada com sucesso!'));
  }, [onConnect, t]);

  // --- handleClose ---
  const handleClose = useCallback(async () => {
    clearPolling();

    // Se estava na etapa QR e não conectou, faz uma verificação final
    if (step === 'QR_CODE' && connectionStatus !== 'CONNECTED' && instanceToken) {
      const toastId = toast.loading(t('Verificando status final da conexão...'));
      try {
        const statusData = await checkInstanceStatus(instanceToken);
        if (statusData.status === 'CONNECTED') {
          await handleSuccessfulConnection(instanceToken, baseInstanceName, uniqueInstanceName);
          toast.dismiss(toastId);
        } else {
          toast.dismiss(toastId);
        }
      } catch {
        toast.dismiss(toastId);
      }
    }

    // Reset state
    setBaseInstanceName('');
    setUniqueInstanceName('');
    setInstanceToken('');
    setManualToken('');
    setManualInstanceName('');
    setTokenVerificationResult(null);
    setConnectMode('QR_CODE');
    setStep('INPUT');
    setQrCodeUrl(undefined);
    setPairCode(undefined);
    setConnectionStatus('DISCONNECTED');

    onClose();
  }, [clearPolling, onClose, step, connectionStatus, instanceToken, baseInstanceName, uniqueInstanceName, handleSuccessfulConnection, t]);

  // Função para monitorar o status da conexão por polling no fluxo de QR Code
  const startStatusPolling = useCallback((token: string, name: string, uniqueName: string) => {
    const pollStatus = async () => {
      try {
        const statusData = await checkInstanceStatus(token);
        setConnectionStatus(statusData.status);

        if (statusData.status === 'CONNECTED') {
          clearPolling();
          await handleSuccessfulConnection(token, name, uniqueName);
          await handleClose();
        } else if (statusData.status === 'ERROR') {
          clearPolling();
          toast.error(t('A conexão falhou. Tente novamente.'));
          setStep('INPUT');
        }
      } catch {
        // Continua tentando em caso de erro de rede
        if (process.env.NODE_ENV === 'development') {
          console.warn('[UAZAPI] Status poll failed, retrying...');
        }
      }
    };

    if (statusPollingRef.current) clearInterval(statusPollingRef.current);
    statusPollingRef.current = window.setInterval(pollStatus, POLLING_INTERVAL_STATUS);
    pollStatus(); // Executa imediatamente
  }, [clearPolling, handleSuccessfulConnection, handleClose, t]);

  const handleGenerateQrCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!baseInstanceName.trim()) return;

    setIsGenerating(true);
    setQrCodeUrl(undefined);
    setPairCode(undefined);
    setConnectionStatus('QRCODE');
    clearPolling();

    try {
      // Step 1: Gerar nome único
      const name = uniqueInstanceName || await getUniqueInstanceName(baseInstanceName.trim());
      setUniqueInstanceName(name);

      // Step 2: Criar instância na UAZAPI
      const { instanceToken: token } = await createInstance(name);
      setInstanceToken(token);

      // Step 3: Conectar a instância (gera QR code)
      const connectResult = await connectInstance(token);

      if (connectResult.qrCodeUrl) {
        setQrCodeUrl(connectResult.qrCodeUrl);
      } else if (connectResult.pairCode) {
        setPairCode(connectResult.pairCode);
      }

      setStep('QR_CODE');

      // Step 4: Iniciar polling de status e timer do QR
      startStatusPolling(token, baseInstanceName.trim(), name);
      startQrTimer();

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('Erro desconhecido ao gerar QR Code.');
      toast.error(errorMessage);
      setStep('INPUT');
      clearPolling();
    } finally {
      setIsGenerating(false);
    }
  };

  // Regenerar QR: reconecta a instância existente
  const handleRegenerateQrCode = async () => {
    if (isGenerating || !instanceToken) return;

    setIsGenerating(true);
    setQrCodeUrl(undefined);
    setPairCode(undefined);
    clearPolling();

    try {
      const connectResult = await connectInstance(instanceToken);

      if (connectResult.qrCodeUrl) {
        setQrCodeUrl(connectResult.qrCodeUrl);
        toast.success(t('Novo QR Code gerado com sucesso!'));
      } else if (connectResult.pairCode) {
        setPairCode(connectResult.pairCode);
        toast.success(t('Novo código de pareamento gerado!'));
      } else {
        toast.error(t('Não foi possível gerar um novo QR Code.'));
        setStep('INPUT');
        return;
      }

      startStatusPolling(instanceToken, baseInstanceName, uniqueInstanceName);
      startQrTimer();

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('Erro ao regenerar QR Code.');
      toast.error(errorMessage);
      setStep('INPUT');
      clearPolling();
    } finally {
      setIsGenerating(false);
    }
  };

  // Fluxo de conexão com Token Manual
  const handleManualTokenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = manualToken.trim();
    if (!token) {
      toast.error(t('integrations.modal_whatsapp_token_required'));
      return;
    }

    setIsValidatingToken(true);
    setTokenVerificationResult(null);

    try {
      // Usa a rota já existente para verificar se a instância está ativa com o token digitado
      const statusData = await checkInstanceStatus(token);
      const name = manualInstanceName.trim() || statusData.instanceName || 'WhatsApp';
      const uniqueName = statusData.instanceName || name;

      if (statusData.status === 'CONNECTED') {
        await handleSuccessfulConnection(token, name, uniqueName);
        handleClose();
      } else if (statusData.status === 'QRCODE') {
        setTokenVerificationResult({
          status: 'QRCODE',
          instanceName: statusData.instanceName,
          message: t('integrations.modal_whatsapp_token_status_qrcode')
        });
        toast(t('integrations.modal_whatsapp_token_status_qrcode'), { icon: '⚠️' });
      } else if (statusData.status === 'DISCONNECTED') {
        setTokenVerificationResult({
          status: 'DISCONNECTED',
          instanceName: statusData.instanceName,
          message: t('integrations.modal_whatsapp_token_status_disconnected')
        });
        toast.error(t('integrations.modal_whatsapp_token_status_disconnected'));
      } else {
        setTokenVerificationResult({
          status: 'ERROR',
          message: t('integrations.modal_whatsapp_token_status_error')
        });
        toast.error(t('integrations.modal_whatsapp_token_status_error'));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('integrations.modal_whatsapp_token_status_error');
      setTokenVerificationResult({
        status: 'ERROR',
        message: msg
      });
      toast.error(msg);
    } finally {
      setIsValidatingToken(false);
    }
  };

  // Permite salvar o token mesmo se a instância estiver pendente de autenticação no momento
  const handleForceSaveManualToken = async () => {
    const token = manualToken.trim();
    if (!token) return;

    const name = manualInstanceName.trim() || tokenVerificationResult?.instanceName || 'WhatsApp';
    const uniqueName = tokenVerificationResult?.instanceName || name;

    try {
      await saveCredential('whatsapp', {
        api_key: token,
        is_active: false,
        instance_details: {
          name,
          uniqueName,
          status: tokenVerificationResult?.status?.toLowerCase() || 'disconnected'
        }
      });
      onConnect(name, token);
      toast.success(t('integrations.modal_whatsapp_token_saved_pending'));
      handleClose();
    } catch {
      toast.error(t('Falha ao salvar a configuração de WhatsApp.'));
    }
  };

  // Limpa o polling ao fechar o modal
  useEffect(() => {
    if (!isOpen) {
      clearPolling();
    }
    return () => clearPolling();
  }, [isOpen, clearPolling]);

  if (!isOpen) return null;

  const isQrReady = step === 'QR_CODE' && (qrCodeUrl || pairCode);

  const getStatusMessage = () => {
    switch (connectionStatus) {
      case 'CONNECTED':
        return { text: t('Conectado com sucesso!'), color: 'bg-green-500' };
      case 'QRCODE':
        return { text: t('Aguardando leitura do QR Code...'), color: 'bg-yellow-500 animate-pulse' };
      case 'DISCONNECTED':
        return { text: t('Desconectado'), color: 'bg-gray-400' };
      case 'ERROR':
      case 'TIMEOUT':
        return { text: t('Erro de conexão. Tente novamente.'), color: 'bg-red-500' };
      default:
        return { text: t('Verificando status...'), color: 'bg-gray-400' };
    }
  };

  const statusInfo = getStatusMessage();

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gradient-to-br from-gray-900/80 to-gray-800/60 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={handleClose}
    >
      <motion.div
        className="relative w-full max-w-md rounded-xl bg-white dark:bg-background-dark shadow-2xl border border-gray-200 dark:border-gray-700/50 p-6 flex flex-col gap-5"
        initial={{ y: 50, opacity: 0, scale: 0.95 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 50, opacity: 0, scale: 0.95 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="text-xl font-bold text-[#181411] dark:text-white">{t('integrations.modal_whatsapp_title')}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {connectMode === 'MANUAL_TOKEN'
                ? t('integrations.modal_whatsapp_subtitle_token')
                : step === 'INPUT'
                ? t('integrations.modal_whatsapp_subtitle_input')
                : t('integrations.modal_whatsapp_subtitle_qr', { instanceName: baseInstanceName })
              }
            </p>
          </div>
          <motion.button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10"
            whileHover={{ rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">close</span>
          </motion.button>
        </div>

        {/* Abas de Conexão (somente exibidas na etapa inicial) */}
        {step === 'INPUT' && (
          <div className="flex rounded-lg bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => {
                setConnectMode('QR_CODE');
                setTokenVerificationResult(null);
              }}
              className={`flex-1 py-2 text-xs font-semibold rounded-md transition-all flex items-center justify-center gap-1.5 ${
                connectMode === 'QR_CODE'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <span className="material-symbols-outlined text-base">qr_code_2</span>
              {t('integrations.modal_whatsapp_tab_qr')}
            </button>
            <button
              type="button"
              onClick={() => {
                setConnectMode('MANUAL_TOKEN');
                setTokenVerificationResult(null);
              }}
              className={`flex-1 py-2 text-xs font-semibold rounded-md transition-all flex items-center justify-center gap-1.5 ${
                connectMode === 'MANUAL_TOKEN'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <span className="material-symbols-outlined text-base">key</span>
              {t('integrations.modal_whatsapp_tab_token')}
            </button>
          </div>
        )}

        {/* MODO 1: QR CODE */}
        {connectMode === 'QR_CODE' && (
          <form onSubmit={handleGenerateQrCode} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="instance_name">
                {t('integrations.modal_whatsapp_instance_name')}
              </label>
              <input
                className={`block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800/50 px-4 py-2.5 text-sm text-[#181411] dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-primary focus:ring-primary ${step === 'QR_CODE' ? 'opacity-70 cursor-not-allowed' : ''}`}
                id="instance_name"
                placeholder={t('integrations.modal_whatsapp_instance_placeholder')}
                type="text"
                value={baseInstanceName}
                onChange={(e) => setBaseInstanceName(e.target.value)}
                disabled={step === 'QR_CODE'}
                required
              />
            </div>

            {/* QR Code / Pair Code Section */}
            {step === 'QR_CODE' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3 }}
                className="flex flex-col items-center justify-center p-4 border border-gray-200 dark:border-gray-700/50 rounded-lg bg-gray-50 dark:bg-gray-900/50 mt-1"
              >
                {qrCodeUrl ? (
                  <motion.img
                    key={qrCodeUrl}
                    src={qrCodeUrl}
                    alt="QR Code WhatsApp"
                    className="w-48 h-48 object-contain"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                  />
                ) : pairCode ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center gap-2"
                  >
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('Código de Pareamento')}</p>
                    <p className="text-3xl font-bold tracking-widest text-primary font-mono">{pairCode}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                      {t('Digite este código no WhatsApp em: Dispositivos conectados → Conectar dispositivo → Conectar com número de telefone')}
                    </p>
                  </motion.div>
                ) : (
                  <div className="w-48 h-48 flex flex-col items-center justify-center gap-3 text-gray-500 dark:text-gray-400">
                    <span className="material-symbols-outlined text-5xl animate-spin">sync</span>
                    <p className="text-sm">{t('integrations.modal_whatsapp_qr_pending')}</p>
                  </div>
                )}

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
                  {t('integrations.modal_whatsapp_qr_instructions')}
                </p>

                {/* Timer do QR */}
                {isQrReady && connectionStatus === 'QRCODE' && qrTimer > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center font-medium">
                    {t('integrations.modal_whatsapp_qr_refresh', { seconds: qrTimer })}
                  </p>
                )}

                {qrTimer === 0 && connectionStatus === 'QRCODE' && (
                  <p className="text-xs text-red-500 mt-2 text-center font-medium">
                    {t('QR Code expirado. Gere um novo.')}
                  </p>
                )}

                {/* Status Indicator */}
                <div className="mt-4 flex items-center gap-2 text-sm font-medium">
                  <span className={`size-2 rounded-full ${statusInfo.color}`}></span>
                  <p className="text-gray-600 dark:text-gray-400">
                    {statusInfo.text}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Footer / Actions QR */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
              {step === 'QR_CODE' && (
                <motion.button
                  type="button"
                  onClick={handleRegenerateQrCode}
                  disabled={isGenerating || connectionStatus === 'CONNECTED'}
                  className="flex h-11 w-full sm:w-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-700/50 text-gray-800 dark:text-gray-200 text-sm font-bold leading-normal tracking-[0.015em] px-6 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: (isGenerating || connectionStatus === 'CONNECTED') ? 1 : 1.05 }}
                  whileTap={{ scale: (isGenerating || connectionStatus === 'CONNECTED') ? 1 : 0.95 }}
                >
                  {isGenerating ? (
                    <span className="material-symbols-outlined animate-spin">sync</span>
                  ) : (
                    t('Gerar Novo QR Code')
                  )}
                </motion.button>
              )}

              <motion.button
                type="button"
                onClick={handleClose}
                className="flex h-11 w-full sm:w-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm font-bold leading-normal tracking-[0.015em] px-6 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {step === 'INPUT' ? t('modals.cancel') : t('dashboard.detail_close')}
              </motion.button>

              {step === 'INPUT' && (
                <motion.button
                  type="submit"
                  className="flex h-11 w-full sm:w-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gradient-primary text-white gap-2 text-sm font-bold leading-normal tracking-[0.015em] px-6 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: isGenerating ? 1 : 1.05 }}
                  whileTap={{ scale: isGenerating ? 1 : 0.95 }}
                  disabled={!baseInstanceName.trim() || isGenerating}
                >
                  {isGenerating ? (
                    <>
                      <span className="material-symbols-outlined animate-spin">sync</span>
                      {t('integrations.loading_integration')}
                    </>
                  ) : (
                    t('integrations.modal_whatsapp_generate_qr')
                  )}
                </motion.button>
              )}
            </div>
          </form>
        )}

        {/* MODO 2: TOKEN MANUAL */}
        {connectMode === 'MANUAL_TOKEN' && (
          <form onSubmit={handleManualTokenSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="manual_instance_name">
                {t('integrations.modal_whatsapp_instance_name')}
              </label>
              <input
                id="manual_instance_name"
                type="text"
                className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800/50 px-4 py-2.5 text-sm text-[#181411] dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-primary focus:ring-primary"
                placeholder={t('integrations.modal_whatsapp_instance_placeholder')}
                value={manualInstanceName}
                onChange={(e) => setManualInstanceName(e.target.value)}
                disabled={isValidatingToken}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="manual_token">
                {t('integrations.modal_whatsapp_token_label')}
              </label>
              <input
                id="manual_token"
                type="text"
                required
                className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800/50 px-4 py-2.5 text-sm font-mono text-[#181411] dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-primary focus:ring-primary"
                placeholder={t('integrations.modal_whatsapp_token_placeholder')}
                value={manualToken}
                onChange={(e) => setManualToken(e.target.value)}
                disabled={isValidatingToken}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('integrations.modal_whatsapp_token_help')}
              </p>
            </div>

            {/* Diagnóstico em tempo real se o status não for CONNECTED */}
            {tokenVerificationResult && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-3 rounded-lg border text-sm flex flex-col gap-2 ${
                  tokenVerificationResult.status === 'CONNECTED'
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                    : tokenVerificationResult.status === 'QRCODE'
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
                }`}
              >
                <div className="flex items-center gap-2 font-medium">
                  <span className="material-symbols-outlined text-lg">
                    {tokenVerificationResult.status === 'CONNECTED'
                      ? 'check_circle'
                      : tokenVerificationResult.status === 'QRCODE'
                      ? 'qr_code_scanner'
                      : 'error'}
                  </span>
                  <span>{tokenVerificationResult.message}</span>
                </div>
                {tokenVerificationResult.status !== 'CONNECTED' && (
                  <button
                    type="button"
                    onClick={handleForceSaveManualToken}
                    className="text-xs font-semibold underline text-left hover:opacity-80 transition-opacity mt-1"
                  >
                    {t('integrations.modal_whatsapp_token_save_anyway')}
                  </button>
                )}
              </motion.div>
            )}

            {/* Footer / Actions Token Manual */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
              <motion.button
                type="button"
                onClick={handleClose}
                disabled={isValidatingToken}
                className="flex h-11 w-full sm:w-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm font-bold leading-normal tracking-[0.015em] px-6 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('modals.cancel')}
              </motion.button>
              <motion.button
                type="submit"
                disabled={!manualToken.trim() || isValidatingToken}
                className="flex h-11 w-full sm:w-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gradient-primary text-white gap-2 text-sm font-bold leading-normal tracking-[0.015em] px-6 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: isValidatingToken ? 1 : 1.05 }}
                whileTap={{ scale: isValidatingToken ? 1 : 0.95 }}
              >
                {isValidatingToken ? (
                  <>
                    <span className="material-symbols-outlined animate-spin">sync</span>
                    {t('integrations.modal_whatsapp_verifying')}
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-lg">verified</span>
                    {t('integrations.modal_whatsapp_btn_verify')}
                  </>
                )}
              </motion.button>
            </div>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
};

export default WhatsappConnectModal;